import React, { useState, useRef, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { 
    Flex, Box, Heading, FormControl, FormLabel, Input, Button, ButtonGroup, Text, CircularProgress,
    InputGroup, InputRightElement, Icon, VStack, Divider,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, useDisclosure,
    UnorderedList, OrderedList, ListItem, Link, useToast
} from '@chakra-ui/react';
import { MdSave, MdCancel, MdFileUpload } from 'react-icons/md';

import { ExcelRenderer } from 'react-excel-renderer/build';
import axios from 'axios';
import Papa from 'papaparse';

import { ERROR_CODES } from '../../../constants';

function ImportExcelServicePriceModal({ 
    initialRef, isOpen, onClose, company_id, onImportDataFinished
}) {
    const fileInputRef = useRef();
    const [ isLoading, setIsLoading ] = useState(false);
    const [ uploadedData, setUploadedData ] = useState([]);
    const [ isSavedDb, setIsSavedDb ] = useState(false);
    const [ totalSuccessAdd, setTotalSuccessAdd ] = useState(0);
    const [ totalFailAdd, setTotalFailAdd ] = useState(0);
    const [ errorFailAdd, setErrorFailAdd ] = useState([]);
    const toast = useToast();

    const fileHandler = (event) => {
        if(event.target.files.length) {
            let fileObj = event.target.files[0];
            let fileName = fileObj.name;
            let isLt10M = fileObj.size / 1024 / 1024 < 10;
            const fileType = fileName.slice(fileName.lastIndexOf('.') + 1);

            if(!isLt10M) {
                toast({
                    description: "Bạn chỉ có thể upload file < 10Mb!",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                })
                return;
            }

            if(!(fileType === "xlsx" || fileType === "csv")) {
                toast({
                    description: "Bạn chỉ có thể upload file EXCEL (.xlsx) hoặc CSV (.csv)!",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                    position: "bottom-right"
                })
                return;
            }

            if(fileType === "xlsx") {
                renderExcelFile(fileObj);
            } else if(fileType === "csv") {
                renderCSVFile(fileObj);
            }
        }
    }

    const renderExcelFile = (fileObj) => {
        ExcelRenderer(fileObj, (err, resp) => {
            if(err) {
                console.log('---- error', err);
                setUploadedData([]);
            } else {
                setUploadedData(resp.rows);
            }
        })
    }

    const renderCSVFile = (fileObj) => {
        Papa.parse(fileObj, {
            complete: (result) => {
                setIsSavedDb(false);
                setUploadedData(result.data);
            },
            header: false
        })
    }

    const handleDownloadSampleExcel = () => {
        let a = document.createElement("a");
        a.href = `${process.env.REACT_APP_WEB_SERVER_URL}/sampleFiles/mauDichVuExcel.xlsx`;
        a.download = `mauDichVuExcel.xlsx`;
        a.click();
    }

    const handleSaveExcelFile = async() => {
        setIsLoading(true);
        let totalSuccessAdd = 0, totalFailAdd = 0;
        const error = [];

        uploadedData && uploadedData.map(async(item, idx) => {
            if(idx !== 0) {
                setTimeout(async() => {
                    try
                    {
                        const res =  await axios.post(`${process.env.REACT_APP_API_ENDPOINT_URL}/service_prices`, {
                            company_id,
                            name: item[0],
                            price: parseFloat(item[1]),
                            is_initial_fee: item[2],
                        }, {
                            auth: {
                                username: process.env.REACT_APP_BASIC_AUTH_USERNAME,
                                password: process.env.REACT_APP_BASIC_AUTH_PASSWORD
                            }
                        })
        
                        if(res.data?.status === 200) {
                            totalSuccessAdd += 1;
                        } else {
                            totalFailAdd += 1;
                            error.push({
                                code: item[0],
                                msg: res.data.msg
                            });
                        }
        
                        if(idx === uploadedData.length - 1) {
                            setUploadedData([]);
                            setIsSavedDb(true);
                            setTotalSuccessAdd(totalSuccessAdd);
                            setTotalFailAdd(totalFailAdd);
                            setErrorFailAdd(error);
                            setIsLoading(false);
        
                            if(onImportDataFinished) {
                                onImportDataFinished();
                            }
                        }
                    }
                    catch(error) {
                        if(error?.code) {
                            toast({
                                description: `${ERROR_CODES[error?.code]} (${error?.code})`,
                                status: "error",
                                duration: 3000,
                                isClosable: true,
                                position: "bottom-right"
                            });
                        }
                    }
                }, 100*idx);
            }
        });
    }

    return (
        <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    NHẬT DỮ LIỆU NHÂN VIÊN TỪ FILE
                </ModalHeader>
                <ModalCloseButton />

                <ModalBody pb={6}>
                    <Text as="b">Để nhập dữ liệu từ file thực hiện các bước sau:</Text>
                    <OrderedList>
                        <ListItem>Nhấp vào nút 'UPLOAD FILE' và chọn file cần upload</ListItem>
                        <ListItem>Sau khi hệ thống tải xong file, nhấn nút 'LƯU' để hoàn tất</ListItem>
                    </OrderedList>
                    <Text as="i" mt={4}>
                        Lưu ý: File tải lên cần theo đúng định dạng, nhấn 
                        <Link href='#' color="blue.700" onClick={handleDownloadSampleExcel}>
                            {` DOWNLOAD EXCEL MẪU`}
                        </Link> để tải file mẫu
                        <input type="file" accept='.xlsx,.csv' ref={fileInputRef} hidden style={{ padding: "10px"}}
                            onChange={fileHandler} 
                            onClick={(event) => {event.target.value = null}} 
                        />
                    </Text>

                    {
                        (uploadedData.length - 1 > 0) && <Text fontSize="16px" fontWeight="700">Tổng số Data: {uploadedData.length - 1}</Text>
                    }

                    {
                        isSavedDb && 
                        <React.Fragment>
                            <Text fontSize="16px" fontWeight="700" color="green.500">
                                Thêm thành công: {totalSuccessAdd}
                            </Text>
                            <Text fontSize="16px" fontWeight="700" color="red.500">
                                Thêm không thành công: {totalFailAdd}
                            </Text>
                            {
                                errorFailAdd && errorFailAdd.map((item, idx) => (
                                    <Text key={`error-${idx}`} fontSize="16px" fontWeight="700" color="red.500">
                                        {`${item.code}: ${item.msg}`}
                                    </Text>
                                ))
                            }
                        </React.Fragment>
                    }
                </ModalBody>

                <ModalFooter>  
                    <Flex gap={2}>  
                        {
                            (uploadedData.length === 0) &&
                            <Button colorScheme='orange'
                                onClick={() => {
                                        fileInputRef.current.click();
                                    }
                                }
                            >
                                <Icon boxSize={6} as={MdFileUpload} marginRight="8px"/>   
                                <Text fontSize="14px" fontWeight="400">UPLOAD FILE</Text>
                            </Button>
                        }

                        {
                            (uploadedData.length !== 0) &&
                            <Button colorScheme='red'
                                isLoading={isLoading}
                                loadingText="Đang lưu"
                                onClick={
                                    () => {
                                        setUploadedData([]);
                                    }
                                }
                            >
                                <Icon boxSize={6} as={MdCancel} marginRight="8px"/>   
                                <Text fontSize="14px" fontWeight="400">HUỶ UPLOAD</Text>
                            </Button>
                        }

                        {
                            (uploadedData.length !== 0) &&
                            <Button colorScheme='blue' 
                                isLoading={isLoading}
                                loadingText="Đang lưu"
                                onClick={handleSaveExcelFile}
                            >
                                <Icon boxSize={6} as={MdSave} marginRight="8px"/>   
                                <Text fontSize="14px" fontWeight="400">LƯU</Text>
                            </Button>
                        }
                    </Flex> 
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default ImportExcelServicePriceModal;